import { Box, Divider, Stack, Switch, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { prop, sortBy, unique } from 'remeda';
import { DynamicIcon, Loader } from 'src/components';
import { type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import { WeekSchedule, WeekScheduleFormFields } from 'src/components/WeekSchedule';
import { TIME_FORMAT } from 'src/constants/date';
import {
  SignalBusinessHoursEntity,
  SignalUrgencyLevel,
  useDeleteSignalBusinessHours,
  WeekDay,
} from 'src/graphql';
import { useCreateSignalBusinessHours } from 'src/graphql/entities/signal-business-hours/hooks/use-create-signal-business-hours';
import { useDefaultSignalBusinessHours } from 'src/graphql/entities/signal-business-hours/hooks/use-default-signal-business-hours';
import { useUpdateSignalBusinessHours } from 'src/graphql/entities/signal-business-hours/hooks/use-update-signal-business-hours';
import { ApplyToAllDays } from 'src/pages/Settings/shared/apply-to-all-days';

export type UrgencyLevel = {
  name: string;
  description: string;
  urgency: SignalUrgencyLevel;
  icon: string;
  iconLibrary: IconLibrary;
  iconProps?: {
    color: string;
  };
  canDisable?: boolean;
};

export type UrgencyTabProps = UrgencyLevel & {
  onUrgencyEnabledChange: (id: SignalUrgencyLevel, enabled: boolean) => void;
  signalBusinessHours: SignalBusinessHoursEntity[];
  enabled: boolean;
  isLoading: boolean;
};

const mapSignalBusinessHourToFormValue = (it: Partial<SignalBusinessHoursEntity>) => ({
  id: it.signalBusinessHoursId || '',
  weekDay: it.weekDay ?? 0,
  createdAt: it.createdAt!,
  from: `0000-00-00T${it.timeFrom || ''}`,
  to: `0000-00-00T${it.timeTo || ''}`,
});

const normalizeSignalBusinessHours = (signalBusinessHours: SignalBusinessHoursEntity[]) => {
  return sortBy(signalBusinessHours, [prop('weekDay'), 'asc'], [prop('timeFrom'), 'asc']).map(
    mapSignalBusinessHourToFormValue,
  );
};

export const UrgencyTab = (props: UrgencyTabProps) => {
  const {
    name,
    urgency,
    signalBusinessHours,
    enabled,
    onUrgencyEnabledChange,
    icon,
    iconLibrary,
    iconProps = {},
    description,
    canDisable = true,
    isLoading,
  } = props;
  const { defaultSignalBusinessHours } = useDefaultSignalBusinessHours();
  const { createSignalBusinessHours } = useCreateSignalBusinessHours();
  const { updateSignalBusinessHours } = useUpdateSignalBusinessHours();
  const { deleteSignalBusinessHoursById } = useDeleteSignalBusinessHours();

  // Key to force WeekSchedule remount after bulk apply - incremented manually after bulk operations
  const [scheduleKey, setScheduleKey] = useState(0);

  // Track if ApplyToAllDays should be shown - once shown, keep it visible to preserve state
  const [showApplyToAll, setShowApplyToAll] = useState(signalBusinessHours.length > 0);

  // Show ApplyToAllDays when there are active days (but don't hide it during bulk operations)
  useEffect(() => {
    if (signalBusinessHours.length > 0 && !showApplyToAll) {
      setShowApplyToAll(true);
    }
  }, [signalBusinessHours.length, showApplyToAll]);

  // Get unique active days
  const activeDays = useMemo(
    () => unique(signalBusinessHours.map((item) => item.weekDay)),
    [signalBusinessHours],
  );

  const onTabChange = useCallback(
    (_: SyntheticEvent, checked: boolean) => {
      onUrgencyEnabledChange(urgency, checked);
    },
    [onUrgencyEnabledChange, urgency],
  );

  const onDeleteTimeWindow = useCallback(
    (ids: string | string[]) => {
      void deleteSignalBusinessHoursById(Array.isArray(ids) ? ids : [ids]);
    },
    [deleteSignalBusinessHoursById],
  );

  const onAddTimeWindow = useCallback(
    (dayIndex: number) => {
      const tempId = `temp-${crypto.randomUUID()}`;
      const defaultHours = defaultSignalBusinessHours
        .find((item) => item.urgency === urgency)
        ?.signalBusinessHours.find((item) => item.weekday === (dayIndex as WeekDay));
      const fallbackDefaultStartTime = dayjs(`0000-00-00T09:00`);
      const fallbackDefaultEndTime = dayjs(`0000-00-00T17:00`);
      const data = {
        urgency,
        weekDay: dayIndex,
        timeFrom: defaultHours?.from || fallbackDefaultStartTime,
        timeTo: defaultHours?.to || fallbackDefaultEndTime,
        createdAt: dayjs().toISOString(),
      };
      return {
        create: async () => {
          const result = await createSignalBusinessHours(data);
          return result?.data?.createSignalBusinessHours?.[0] as SignalBusinessHoursEntity;
        },
        optimisticResponse: {
          ...data,
          id: tempId,
          signalBusinessHoursId: tempId,
          timeFrom: data.timeFrom.format(TIME_FORMAT),
          timeTo: data.timeTo.format(TIME_FORMAT),
        },
      };
    },
    [createSignalBusinessHours, defaultSignalBusinessHours, urgency],
  );

  const onTimeChange = useCallback(
    (id: string, inputType: 'from' | 'to', value: Dayjs | null) => {
      if (id && value) {
        void updateSignalBusinessHours(
          id,
          inputType === 'from' ? 'timeFrom' : 'timeTo',
          value,
        );
      }
    },
    [updateSignalBusinessHours],
  );

  // Memoize IDs to avoid callback reference changes on every signalBusinessHours update
  const idsToDelete = useMemo(
    () => signalBusinessHours.map((item) => item.signalBusinessHoursId),
    [signalBusinessHours],
  );

  // Apply time windows to all active days
  const onApplyToAll = useCallback(
    async (timeWindows: { from: Dayjs; to: Dayjs }[]) => {
      try {
        // Delete all existing time windows for active days (single mutation)
        if (idsToDelete.length) {
          await deleteSignalBusinessHoursById(idsToDelete);
        }

        // Create all new time windows in parallel
        const createPromises = activeDays.flatMap((weekDay) =>
          timeWindows.map((tw) =>
            createSignalBusinessHours({
              weekDay,
              timeFrom: dayjs(`0000-00-00T${tw.from.format(TIME_FORMAT)}`),
              timeTo: dayjs(`0000-00-00T${tw.to.format(TIME_FORMAT)}`),
              urgency,
            }),
          ),
        );
        await Promise.all(createPromises);

        // Force WeekSchedule to remount with fresh data after bulk operation completes
        setScheduleKey((prev) => prev + 1);
      } catch (error) {
        // Error is already handled by the mutation hooks (shows error alert)
        // Don't increment scheduleKey on failure to avoid showing stale data
        console.error('Failed to apply time windows to all days:', error);
      }
    },
    [
      idsToDelete,
      activeDays,
      deleteSignalBusinessHoursById,
      createSignalBusinessHours,
      urgency,
    ],
  );

  const formValues = useMemo(() => {
    return {
      [WeekScheduleFormFields.Schedule]: normalizeSignalBusinessHours(signalBusinessHours),
    };
  }, [signalBusinessHours]);

  return (
    <Stack>
      <Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
        <DynamicIcon
          name={icon}
          library={iconLibrary}
          size={32}
          {...iconProps}
          sx={{ fontSize: '2rem', width: '2rem' }}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6">{name}</Typography>
          <Typography variant="subtitle1">{description}</Typography>
        </Box>
        {canDisable ? <Switch onChange={onTabChange} checked={enabled} /> : null}
      </Stack>
      <Divider flexItem sx={{ mt: '1.5rem', mb: '1.5rem' }} />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {showApplyToAll ? (
            <ApplyToAllDays
              onApply={onApplyToAll}
              hasActiveDays={activeDays.length > 0}
              maxTimeWindows={3}
            />
          ) : null}
          <WeekSchedule
            key={scheduleKey}
            values={formValues}
            turnedOffDateLabel="Outside of escalation hours"
            maxTimeWindows={3}
            onDeleteTimeWindow={onDeleteTimeWindow}
            onAddTimeWindow={onAddTimeWindow}
            onChangeTimeWindow={onTimeChange}
            mapEntityToFormValue={mapSignalBusinessHourToFormValue}
          />
        </>
      )}
      <Divider flexItem sx={{ mt: '1.5rem' }} />
    </Stack>
  );
};
